import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "controls-container"
}
const _hoisted_2 = { class: "path-buttons" }
const _hoisted_3 = { class: "buttons-label-container" }
const _hoisted_4 = { class: "buttons-label" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "buttons-label-container" }
const _hoisted_7 = { class: "buttons-label" }
const _hoisted_8 = {
  key: 0,
  class: "checkbox-row"
}
const _hoisted_9 = { class: "path-buttons-small-screen" }
const _hoisted_10 = ["onClick"]

import UnderlineTabGroup from '@/design-system/molecules/UnderlineTabGroup.vue'
import { computed, inject, nextTick, ref, toRefs, watch } from 'vue'
import CardInteractive from './CardInteractive.vue'
import ChartBase from './ChartComponent/ChartBase.vue'
import { useI18n } from 'vue-i18n'
import { sharedDataKey } from '../../types'
import { useTimeSeries, TimeSeriesConfig } from './ChartComponent/chart/timeSeries'
import CollapseControllerSummaryNew from './CollapseController/CollapseControllerSummaryNew.vue'
import { ColorScaleConfig } from '@/utils/colorScale'
import { FormatOptions } from '../formatter'
import { useEntityLookup } from '@/composables/useEntityLookup'
import GraphCardCheckbox from './cards/GraphCardCheckbox.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'

export type Props = {
  featureName: string
  title: string
  info?: string
  enableFullscreen?: boolean
  chartHeight?: string
  cardHeight?: string
  fields: string[]
  colorScale?: ColorScaleConfig
  dataFormat?: FormatOptions
}

type SeriesIdentifier = { path: string | null; field: string }

export default /*@__PURE__*/_defineComponent({
  __name: 'TimeSeriesCard',
  props: {
    featureName: {},
    title: {},
    info: { default: undefined },
    enableFullscreen: { type: Boolean },
    chartHeight: { default: '100%' },
    cardHeight: { default: '400px' },
    fields: {},
    colorScale: { default: undefined },
    dataFormat: { default: undefined }
  },
  setup(__props: any) {

const props = __props
const sharedData = inject(sharedDataKey)
const dashboardFullscreen = computed(() => sharedData?.fullscreen ?? false)
const { chartHeight, colorScale, dataFormat } = toRefs(props)
const { getEntityDisplayStringFromPath } = useEntityLookup()
const { locale, t } = useI18n()

const showingInfo = ref(false)

const enableResolution = computed(() => true)
const chartBase = ref<InstanceType<typeof ChartBase> | undefined>(undefined)
const chart = computed<TimeSeriesConfig>((): TimeSeriesConfig => {
  return {
    dataSources: [],
    colorScale: colorScale?.value,
    dataFormat: dataFormat.value,
    missingDataMessage: {
      en: 'No data available',
      sv: 'Ingen data tillgänglig'
    }
  }
})

const selectedSeries = ref<SeriesIdentifier[]>([])
const {
  components,
  updateViewport,
  mergedData,
  measurements,
  isLoading,
  resolutionButtons: buttons,
  selectedResolution: selectedName
} = useTimeSeries(chartBase, chart, selectedSeries, props.featureName)

const showLargeCheckboxes = computed(() => fullscreen.value && seriesCheckboxes.value.length > 1)

const hasValidPaths = computed<boolean>(() =>
  mergedData.value.some((d) => d.path !== null && d.path !== undefined)
)

const uniquePathsInData = computed<string[]>(() => {
  if (!hasValidPaths.value) return []
  return [...new Set(mergedData.value.map((d) => d.path).filter((path): path is string => !!path))]
})

const availableSeries = computed<SeriesIdentifier[]>(() => {
  if (hasValidPaths.value) {
    // Paths exist: Combine unique paths with measurement fields
    return uniquePathsInData.value.flatMap((path) =>
      measurements.value.map((m) => ({ path: path, field: m.field.name }))
    )
  } else if (mergedData.value.length > 0) {
    // No paths, but data exists: Series are defined by measurement fields only
    return measurements.value.map((m) => ({ path: null, field: m.field.name }))
  } else {
    // No data at all
    return []
  }
})

const isSingleFieldType = computed(() => measurements.value.length === 1)

const isSeriesSelected = (series: SeriesIdentifier): boolean => {
  return selectedSeries.value.some((s) => s.path === series.path && s.field === series.field)
}

const seriesCheckboxes = computed(() => {
  if (availableSeries.value.length <= 1) {
    // Hide checkboxes if only one series possible
    return []
  }

  return availableSeries.value
    .map((series) => {
      const measurement = measurements.value.find((m) => m.field.name === series.field)
      const measurementTitle = measurement?.title?.[locale.value] ?? series.field

      // Get the entity-specific part of the label using the helper
      const entityDisplayString = getEntityDisplayStringFromPath(series.path) // Call the new helper

      let displayText: string

      if (isSingleFieldType.value) {
        // Single field type: Use entity string if available, otherwise just the (single) measurement title
        displayText = entityDisplayString ?? measurementTitle // Use entity string or fallback to measurement title
      } else {
        // Multiple field types: Combine entity string and measurement title if entity string exists
        if (entityDisplayString) {
          displayText = `${entityDisplayString} - ${measurementTitle}`
        } else {
          // No path/entity string: Just use measurement title
          displayText = measurementTitle
        }
      }

      return {
        id: `${series.path ?? 'no_path'}|${series.field}`,
        text: displayText,
        value: series,
        path: series.path,
        field: series.field,
        color: measurement?.options?.color
      }
    })
    .toSorted((a, b) => a.text.localeCompare(b.text))
})

const handleSeriesSelection = (seriesToToggle: SeriesIdentifier) => {
  const index = selectedSeries.value.findIndex(
    (s) => s.path === seriesToToggle.path && s.field === seriesToToggle.field
  )
  if (index > -1) {
    selectedSeries.value.splice(index, 1)
  } else {
    selectedSeries.value.push(seriesToToggle)
  }
}

const isAllSelected = computed(() => {
  return seriesCheckboxes.value.every((checkbox) => isSeriesSelected(checkbox.value))
})

const selectAll = () => {
  if (isAllSelected.value) {
    selectedSeries.value = []
  } else {
    selectedSeries.value = [...availableSeries.value]
  }
}

const fullscreen = ref(false)

const currentHeight = computed(() => (fullscreen.value ? '600px' : chartHeight.value))

watch(
  availableSeries,
  (newAvailableSeries, oldAvailableSeries) => {
    if (newAvailableSeries.length === 0 && oldAvailableSeries && oldAvailableSeries.length > 0) {
      return
    }

    const preservedSelection = selectedSeries.value.filter((selected) =>
      newAvailableSeries.some(
        (available) => available.path === selected.path && available.field === selected.field
      )
    )

    if (preservedSelection.length === 0 && newAvailableSeries.length > 0) {
      selectedSeries.value = [...newAvailableSeries]
    } else {
      selectedSeries.value = preservedSelection
    }
  },
  { deep: true, immediate: true }
)

function toggleFullscreen(value: boolean) {
  fullscreen.value = value
  if (!value) {
    // Exiting fullscreen: select all available series
    selectedSeries.value = [...availableSeries.value]
  }
  nextTick(() => {
    updateViewport()
  })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['card-chart', { fullscreen: dashboardFullscreen.value }]),
    style: _normalizeStyle({ height: _ctx.cardHeight })
  }, [
    _createVNode(CardInteractive, {
      title: _ctx.title,
      info: _ctx.info,
      enableFullscreen: _ctx.enableFullscreen,
      showControls: fullscreen.value && enableResolution.value,
      isLoading: _unref(isLoading),
      "header-divider": "",
      onToggleFullscreen: toggleFullscreen,
      onToggleShowInfo: _cache[1] || (_cache[1] = (value) => (showingInfo.value = value))
    }, {
      controls: _withCtx(() => [
        _createVNode(UnderlineTabGroup, {
          tabStyle: "pillTabSmall",
          size: "small",
          buttons: _unref(buttons),
          selectedName: _unref(selectedName)
        }, null, 8, ["buttons", "selectedName"])
      ]),
      header: _withCtx(() => [
        (seriesCheckboxes.value.length > 0 && seriesCheckboxes.value.length <= 4 && !fullscreen.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(seriesCheckboxes.value, (btn) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: btn.id,
                    class: "buttons",
                    onClick: 
                (e) => {
                  handleSeriesSelection(btn.value)
                  e.preventDefault()
                }
              
                  }, [
                    _createVNode(GraphCardCheckbox, {
                      color: btn.color ?? 'white',
                      tintColor: "white",
                      checkedColor: btn.color ?? 'blue',
                      "border-color": btn.color ?? 'gray',
                      checked: isSeriesSelected(btn.value)
                    }, null, 8, ["color", "checkedColor", "border-color", "checked"]),
                    _createVNode(TextBase, {
                      text: btn.text,
                      type: 'tiny',
                      style: { 'padding-top': '2px' }
                    }, null, 8, ["text"])
                  ], 8, _hoisted_10))
                }), 128))
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      footerControls: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)('dashboard__text__selection')) + " : ", 1),
        _createVNode(CollapseControllerSummaryNew, {
          showTimeSelection: true,
          fullscreen: fullscreen.value
        }, null, 8, ["fullscreen"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", {
          class: _normalizeClass(['chart-container', { fullscreen: fullscreen.value, checkboxes: showLargeCheckboxes.value }]),
          style: {"height":"100%","width":"100%"}
        }, [
          _createVNode(ChartBase, {
            ref_key: "chartBase",
            ref: chartBase,
            height: currentHeight.value,
            components: _unref(components),
            isFullscreen: fullscreen.value
          }, null, 8, ["height", "components", "isFullscreen"]),
          (showLargeCheckboxes.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  (fullscreen.value)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "buttons select-all",
                        onClick: _cache[0] || (_cache[0] = 
                (e) => {
                  selectAll()
                  e.preventDefault()
                }
              )
                      }, [
                        _createVNode(GraphCardCheckbox, {
                          color: "white",
                          tintColor: "white",
                          checkedColor: "blue",
                          "border-color": "gray",
                          checked: isAllSelected.value
                        }, null, 8, ["checked"]),
                        _createElementVNode("div", _hoisted_3, [
                          _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)('general__data__select-all')), 1)
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(seriesCheckboxes.value, (btn) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: btn.id,
                      class: "buttons",
                      onClick: 
                (e) => {
                  handleSeriesSelection(btn.value)
                  e.preventDefault()
                }
              
                    }, [
                      _createVNode(GraphCardCheckbox, {
                        color: btn.color ?? 'white',
                        tintColor: "white",
                        checkedColor: btn.color ?? 'blue',
                        "border-color": btn.color ?? 'gray',
                        checked: isSeriesSelected(btn.value)
                      }, null, 8, ["color", "checkedColor", "border-color", "checked"]),
                      _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("div", _hoisted_7, _toDisplayString(btn.text), 1)
                      ])
                    ], 8, _hoisted_5))
                  }), 128))
                ])
              ]))
            : _createCommentVNode("", true)
        ], 2)
      ]),
      _: 1
    }, 8, ["title", "info", "enableFullscreen", "showControls", "isLoading"])
  ], 6))
}
}

})